import { UserService } from '../../api/services/UserService';
import type {
  Routemize_DTOs_AddUpdateUserAddressDto,
  Routemize_Constants_AddressType
} from '../../api/models';
import { getRegionCode, isValidRegionCode, isUsOrCanada, getRegionLabel } from '../../utils/addressRegions';
import {
  isUserAddressActive,
  resolvePrimaryUserAddress,
} from '../../utils/userAddressUtils';
import { invalidateTeamMembers } from './teamMembersCache';

/**
 * Extended interface for UI representation of user address
 */
export interface UserAddress {
  id?: string;
  addressType: Routemize_Constants_AddressType;
  addressTypeText?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  zipCode?: string;
  isActive?: boolean;
  latitude?: number;
  longitude?: number;
  fullAddress?: string;
}

/**
 * Request to create or update user addresses
 */
export interface CreateUserAddressRequest {
  userId: string;
  addresses: UserAddress[];
}

/**
 * Address validation result
 */
export interface AddressValidationResult {
  isValid: boolean;
  errors: string[];
  warnings?: string[];
}

/**
 * Address formatting options
 */
export interface AddressFormatOptions {
  includeCountry?: boolean;
  singleLine?: boolean;
  abbreviateState?: boolean;
}

/**
 * AddressManager - Business logic for user address management
 * Handles address validation, formatting, and business operations
 */
export class AddressManager {
  // State/province reference data lives in utils/addressRegions (US + CA). See Canada plan §3.3.

  constructor(private userService: UserService) {}

  /**
   * Get all addresses for a specific user
   */
  async getUserAddresses(userId: string): Promise<UserAddress[]> {
    const addresses = await this.userService.getApiAppUserGetUserAddresses(userId);
    return addresses || [];
  }

  /**
   * Create or update user addresses (bulk operation)
   */
  async createOrUpdateUserAddresses(request: CreateUserAddressRequest): Promise<void> {
    // Validate all addresses before saving
    const validationResults = request.addresses.map(addr => this.validateAddress(addr));
    const hasErrors = validationResults.some(result => !result.isValid);
    
    if (hasErrors) {
      const allErrors = validationResults.flatMap((result, index) => 
        result.errors.map(error => `Address ${index + 1}: ${error}`)
      );
      throw new Error(`Address validation failed: ${allErrors.join(', ')}`);
    }

    // Addresses collected before the user exists carry client-side `temp-…` ids (shared AddressManager component);
    // the API's id is a Guid, so those go out without one and the server inserts them.
    const updateDto: Routemize_DTOs_AddUpdateUserAddressDto = {
      userId: request.userId,
      addresses: request.addresses.map(address =>
        address.id?.startsWith('temp-') ? { ...address, id: undefined } : address
      )
    };

    await this.userService.postApiAppUserCreateUserAddress(updateDto);
    // A user's home address feeds the cached team roster (homeAddress) — refresh it.
    invalidateTeamMembers();
  }

  /**
   * Add a single address to user's existing addresses
   */
  async addUserAddress(userId: string, address: UserAddress): Promise<UserAddress> {
    // Validate the new address
    const validation = this.validateAddress(address);
    if (!validation.isValid) {
      throw new Error(`Address validation failed: ${validation.errors.join(', ')}`);
    }

    // Get existing addresses
    const existingAddresses = await this.getUserAddresses(userId);
    
    // Apply business rules for new addresses
    const processedAddress = this.applyBusinessRules(address, existingAddresses);
    
    // Add new address (no id: the server assigns one on insert)
    const updatedAddresses = [...existingAddresses, processedAddress];

    await this.createOrUpdateUserAddresses({
      userId,
      addresses: updatedAddresses
    });
    
    // Fetch the updated addresses to get the server-generated ID
    const newAddresses = await this.getUserAddresses(userId);
    // Find the newly added address (it should be the one not in existingAddresses)
    const newAddress = newAddresses.find(addr => 
      !existingAddresses.some(existing => existing.id === addr.id)
    ) || processedAddress;
    
    return newAddress;
  }

  /**
   * Update a specific address
   */
  async updateUserAddress(userId: string, addressId: string, updatedAddress: UserAddress): Promise<void> {
    // Validate the updated address
    const validation = this.validateAddress(updatedAddress);
    if (!validation.isValid) {
      throw new Error(`Address validation failed: ${validation.errors.join(', ')}`);
    }

    // Get existing addresses
    const existingAddresses = await this.getUserAddresses(userId);
    
    // Find and update the specific address
    // Explicitly handle addressLine2 to allow clearing it
    const updatedAddresses = existingAddresses.map(addr => {
      if (addr.id === addressId) {
        // Create a new object with all properties from updatedAddress
        // This ensures empty strings are preserved
        const updated = {
          ...addr,
          ...updatedAddress,
          id: addressId,
          // Explicitly set addressLine2 to handle empty string case
          addressLine2: updatedAddress.addressLine2 !== undefined ? updatedAddress.addressLine2 : addr.addressLine2
        };
        return updated;
      }
      return addr;
    });

    this.assertAtLeastOneActiveAddress(updatedAddresses);

    await this.createOrUpdateUserAddresses({
      userId,
      addresses: updatedAddresses
    });
  }

  /**
   * Delete a specific address
   */
  async deleteUserAddress(userId: string, addressId: string): Promise<void> {
    // Get existing addresses first to validate business rules
    const existingAddresses = await this.getUserAddresses(userId);
    
    // Business rule: Don't delete the last active address
    const activeAddresses = existingAddresses.filter(addr => addr.isActive);
    const addressToDelete = existingAddresses.find(addr => addr.id === addressId);
    
    if (addressToDelete?.isActive && activeAddresses.length === 1) {
      throw new Error('Cannot delete the last active address');
    }
    
    // Use the proper delete API endpoint
    await this.userService.deleteApiAppUserDeleteUserAddress(addressId);
  }

  /**
   * Set an address as active/inactive
   */
  async toggleAddressStatus(userId: string, addressId: string, isActive: boolean): Promise<void> {
    // Get existing addresses
    const existingAddresses = await this.getUserAddresses(userId);
    
    // Business rule: At least one address must be active
    if (!isActive) {
      const otherActiveAddresses = existingAddresses.filter(
        addr => addr.id !== addressId && addr.isActive
      );
      if (otherActiveAddresses.length === 0) {
        throw new Error('At least one address must remain active');
      }
    }
    
    // Update the specific address status
    const updatedAddresses = existingAddresses.map(addr => 
      addr.id === addressId ? { ...addr, isActive } : addr
    );

    this.assertAtLeastOneActiveAddress(updatedAddresses);

    await this.createOrUpdateUserAddresses({
      userId,
      addresses: updatedAddresses
    });
  }

  /**
   * Get addresses by type (Home, Work, Other)
   */
  async getUserAddressesByType(userId: string, addressType: Routemize_Constants_AddressType): Promise<UserAddress[]> {
    const addresses = await this.getUserAddresses(userId);
    return addresses.filter(addr => addr.addressType === addressType);
  }

  /**
   * Get the primary (first active) address for a user
   */
  async getPrimaryUserAddress(userId: string): Promise<UserAddress | null> {
    const addresses = await this.getUserAddresses(userId);
    return this.findPrimaryAddress(addresses);
  }

  // Business Logic Methods

  /**
   * Validate address data with comprehensive rules
   */
  validateAddress(address: UserAddress): AddressValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Required field validation
    if (!address.addressLine1?.trim()) {
      errors.push('Address Line 1 is required');
    }

    if (!address.city?.trim()) {
      errors.push('City is required');
    }

    if (!address.state?.trim()) {
      errors.push('State/Province is required');
    }

    if (!address.country?.trim()) {
      errors.push('Country is required');
    }

    // Postal code validation
    if (!address.postalCode?.trim() && !address.zipCode?.trim()) {
      errors.push('Postal Code or ZIP Code is required');
    } else {
      const postalCode = address.postalCode || address.zipCode || '';
      if (!this.validatePostalCode(postalCode, address.country || '')) {
        warnings.push('Postal code format may be invalid for the selected country');
      }
    }

    // Address line length validation
    if (address.addressLine1 && address.addressLine1.length > 100) {
      errors.push('Address Line 1 must be 100 characters or less');
    }

    if (address.addressLine2 && address.addressLine2.length > 100) {
      errors.push('Address Line 2 must be 100 characters or less');
    }

    // State/province code validation for US + Canadian addresses
    if (isUsOrCanada(address.country)) {
      if (!this.isValidStateAbbreviation(address.state || '', address.country)) {
        warnings.push(`${getRegionLabel(address.country)} should be a valid abbreviation`);
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings: warnings.length > 0 ? warnings : undefined
    };
  }

  /**
   * Format address for display
   */
  formatAddress(address: UserAddress, options: AddressFormatOptions = {}): string {
    const parts: string[] = [];
    
    if (address.addressLine1) {
      parts.push(address.addressLine1);
    }
    
    if (address.addressLine2) {
      parts.push(address.addressLine2);
    }
    
    const cityStateZip: string[] = [];
    if (address.city) {
      cityStateZip.push(address.city);
    }
    
    if (address.state) {
      const state = options.abbreviateState
        ? this.getStateAbbreviation(address.state, address.country)
        : address.state;
      cityStateZip.push(state);
    }
    
    const postalCode = address.postalCode || address.zipCode;
    if (postalCode) {
      cityStateZip.push(postalCode);
    }
    
    if (cityStateZip.length > 0) {
      parts.push(cityStateZip.join(', '));
    }
    
    if (options.includeCountry && address.country) {
      parts.push(address.country);
    }
    
    return options.singleLine 
      ? parts.join(', ')
      : parts.join('\n');
  }

  /**
   * Get a display label for the address
   */
  getAddressLabel(address: UserAddress): string {
    const typeLabel = address.addressTypeText || address.addressType || 'Address';
    const location = address.city ? ` - ${address.city}` : '';
    return `${typeLabel}${location}`;
  }

  /**
   * Find the primary address from a list
   */
  findPrimaryAddress(addresses: UserAddress[]): UserAddress | null {
    return resolvePrimaryUserAddress(addresses) ?? null;
  }

  /**
   * Check if two addresses are equivalent (for duplicate detection)
   */
  areAddressesEquivalent(addr1: UserAddress, addr2: UserAddress): boolean {
    const normalize = (str: string | undefined) => (str || '').trim().toUpperCase();
    
    return normalize(addr1.addressLine1) === normalize(addr2.addressLine1) &&
           normalize(addr1.addressLine2) === normalize(addr2.addressLine2) &&
           normalize(addr1.city) === normalize(addr2.city) &&
           normalize(addr1.state) === normalize(addr2.state) &&
           normalize(addr1.postalCode || addr1.zipCode) === normalize(addr2.postalCode || addr2.zipCode) &&
           normalize(addr1.country) === normalize(addr2.country);
  }

  /**
   * Calculate distance between two addresses (simplified - would use geocoding API in production)
   */
  estimateDistance(addr1: UserAddress, addr2: UserAddress): 'same_city' | 'same_state' | 'different_state' | 'unknown' {
    if (!addr1.city || !addr2.city || !addr1.state || !addr2.state) {
      return 'unknown';
    }
    
    const city1 = addr1.city.toUpperCase();
    const city2 = addr2.city.toUpperCase();
    const state1 = this.getStateAbbreviation(addr1.state, addr1.country);
    const state2 = this.getStateAbbreviation(addr2.state, addr2.country);
    
    if (city1 === city2 && state1 === state2) {
      return 'same_city';
    } else if (state1 === state2) {
      return 'same_state';
    } else {
      return 'different_state';
    }
  }

  // Private Helper Methods

  private assertAtLeastOneActiveAddress(addresses: UserAddress[]): void {
    if (!addresses.some(isUserAddressActive)) {
      throw new Error('At least one address must remain active');
    }
  }

  /**
   * Apply business rules to new addresses
   */
  private applyBusinessRules(address: UserAddress, existingAddresses: UserAddress[]): UserAddress {
    // If this is the first address, make it active
    if (existingAddresses.length === 0) {
      address.isActive = true;
    }
    
    // Check for duplicates
    const isDuplicate = existingAddresses.some(existing => 
      this.areAddressesEquivalent(existing, address)
    );
    
    if (isDuplicate) {
      throw new Error('This address already exists');
    }
    
    // Normalize state/province to its 2-letter code for US + Canadian addresses
    if (isUsOrCanada(address.country)) {
      address.state = this.getStateAbbreviation(address.state || '', address.country);
    }
    
    // Set address type text if not provided
    if (!address.addressTypeText) {
      address.addressTypeText = address.addressType;
    }
    
    return address;
  }

  /**
   * Validate postal code format based on country
   */
  private validatePostalCode(postalCode: string, country: string): boolean {
    const countryUpper = country.toUpperCase();

    // US ZIP code validation
    if (countryUpper === 'US' || countryUpper === 'UNITED STATES' || countryUpper === 'USA') {
      return /^\d{5}(-\d{4})?$/.test(postalCode);
    }

    // Canadian postal code validation
    if (countryUpper === 'CA' || countryUpper === 'CANADA' || countryUpper === 'CAN') {
      return /^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/i.test(postalCode);
    }

    // UK postcode validation (simplified)
    if (countryUpper === 'UNITED KINGDOM' || countryUpper === 'UK') {
      return /^[A-Z]{1,2}\d{1,2}\s?\d[A-Z]{2}$/i.test(postalCode);
    }

    // Default: just check if it's not empty
    return postalCode.length > 0;
  }

  /**
   * Check if a string is a valid state/province code for the country (defaults to US).
   */
  private isValidStateAbbreviation(state: string, country?: string): boolean {
    return isValidRegionCode(state, country);
  }

  /**
   * Resolve a state/province name or code to its 2-letter code for the country (defaults to US).
   * Returns the input unchanged if it can't be resolved.
   */
  private getStateAbbreviation(state: string, country?: string): string {
    return getRegionCode(state, country);
  }
}

// Export singleton instance
export const addressManager = new AddressManager(UserService);